import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { CircleHelp, Home, LogOut, MessageCircleQuestion } from 'lucide-react';
import { openAssistant } from '@/help/assistant-store';
import { useAuth } from '@/auth/AuthProvider';
import { LogoMark } from '@/components/Brand';
import { LangToggle } from '@/components/Prefs';
import { useT } from '@/i18n/context';
import { inboxQuery } from '@/lib/student-portal';
import { SandboxBanner } from '@/components/SandboxBanner';
import { NativeDisabledButtons } from '@/components/native-disabled';
import { warmRafifImages } from '@/components/rafif-poses';

/**
 * قشرة الطالب (22 سبتمبر 2026) — **بلا قائمة جانبية**: الصفحةُ الأولى هي القائمة، فلا تتكرّر
 * المربّعاتُ مرتين على الشاشة. الرأس: الشعار (يعود إلى صفحتي) والاسم واللغة وخروج.
 * وصفحةُ التمرين **ملءُ الشاشة**: لا رأسَ ولا هوامش — زرُّ عودةٍ صغير داخلها وحده كي لا يتشتّت الطفل.
 */
export function KidShell() {
  const { me, signOut } = useAuth();
  const t = useT();
  const { pathname } = useLocation();
  const inbox = useQuery(inboxQuery());
  // صورُ رفيف كلُّها تُحمَّل عند أول دخولٍ إلى البوابة — فلا تومض حين تتبدّل أوضاعُها في التمرين
  useEffect(() => { warmRafifImages(); }, []);
  const unread = (inbox.data?.messages ?? 0) + (inbox.data?.notifications.filter((n) => !n.readAt).length ?? 0);
  if (!me) return null;
  const playing = /^\/me\/student\/games\/[^/]+$/.test(pathname);
  if (playing) {
    return (
      <div className="kid kid--full">
        <SandboxBanner />
        <main className="kid-content kid-content--full"><NativeDisabledButtons.Provider value><Outlet /></NativeDisabledButtons.Provider></main>
      </div>
    );
  }
  return (
    <div className="kid kid--bar">
      <header className="kid-header">
        <Link to="/me/student" className="kid-header__brand" aria-label={t('app.brandAria')}>
          <LogoMark size={40} className="kid-brand__mark" />
          <span className="kid-header__brandname">{t('brand.name')}</span>
        </Link>
        {pathname !== '/me/student' && (
          <Link to="/me/student" className="kid-header__home"><Home size={20} aria-hidden /><span>صفحتي</span>{unread > 0 && <span className="kid-tile__badge kid-tile__badge--inline">{unread}</span>}</Link>
        )}
        {/* المساعدةُ بنسختها للطفل — بأصناف رأسه نفسِها (kid.css لا تُمسّ) */}
        {pathname !== '/me/student/help' && (
          <Link to="/me/student/help" className="kid-header__home" aria-label="مساعدة"><CircleHelp size={20} aria-hidden /><span>مساعدة</span></Link>
        )}
        {/* «اسأل» للطفل (29 سبتمبر 2026): بأصناف رأس الطفل نفسِها — kid.css لا تُمسّ */}
        <button type="button" className="kid-header__home kid-header__ask" data-testid="kid-ask" onClick={() => openAssistant()} aria-label="اسأل"><MessageCircleQuestion size={20} aria-hidden /><span>اسأل</span></button>
        <span className="kid-header__name">{me.user.fullName}</span>
        <LangToggle />
        <button type="button" className="kid-signout" onClick={() => void signOut()} aria-label={t('common.signOutLong')}>
          <LogOut size={20} aria-hidden />
          <span>{t('common.signOut')}</span>
        </button>
      </header>
      <SandboxBanner />
      <main className="kid-content"><NativeDisabledButtons.Provider value><Outlet /></NativeDisabledButtons.Provider></main>
    </div>
  );
}
